import CongresOverlay from '@/components/congres-overlay';
import ContentLayout from '@/components/layout/ContentLayout';
import { QuizCarousel } from '@/components/quiz-carousel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { HOMEWORK_STATE } from '@/lib/utils';
import { getQuestionList, getQuestion, getStudentMessage, getQuiz, checkPulseItem, submitPulseItem } from '@/services/generatedAPIs';
import { AssignmentModel, GetQuestionListResponse } from '@/services/generatedAPIs.schemas';
import { useEffect, useState, useRef } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useLocation, NavLink } from 'react-router';
import { useSubjectDisplayName } from '@/hooks/use-subject-name';
import { PulseItemDialog } from '@/components/pulse-item-dialog';
import MobileLandscapeAnswer, { useMobileLandscape } from '@/components/mobile-landscape-answer';
import { ROUTE } from '@/routes/constants';
import { ChevronLeft } from 'lucide-react';

function HomeworkPage({
  t,
  i18n,
  ...props
}: WithTranslation) {
  const { getCurrentUser } = useAuth();
  const locationState = useLocation().state;

  const [assignmentModels, setAssignmentModels] = useState<AssignmentModel[]>(
    Array.isArray(locationState) ? locationState : []
  );
  let [quizMap, setQuizMap] = useState<Map<number, GetQuestionListResponse>>(new Map());
  const [showCongres, setShowCongres] = useState(false);
  let [tabId, setTabId] = useState('');
  const isFetchingRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const isMobileLandscape = useMobileLandscape();

// Pulse item dialog state
const [showPulseDialog, setShowPulseDialog] = useState(false);
const [checkedAssignments, setCheckedAssignments] = useState<Set<number>>(new Set());
const [pulseAssignmentId, setPulseAssignmentId] = useState<number | null>(null);
const [pulseType, setPulseType] = useState<'start' | 'end'>('start');
const [pulseItemId, setPulseItemId] = useState<string>('');
const [shouldShowExpectedDialog, setShouldShowExpectedDialog] = useState(false);
const [completedStartPulseCount, setCompletedStartPulseCount] = useState(0); // 记录已完成的 START pulse 次数


  // Initialize assignmentModels and tabId from locationState
  useEffect(() => {
    if (Array.isArray(locationState) && locationState.length > 0) {
      console.log('Setting assignmentModels from locationState');
      setAssignmentModels(locationState);
      setQuizMap(new Map());
      const firstAssignmentId = locationState[0].assignment.id?.toString() ?? '';
      if (firstAssignmentId) {
        console.log('Setting tabId to:', firstAssignmentId);
        setTabId(firstAssignmentId);
        HOMEWORK_STATE.tabId = firstAssignmentId;
      }
      hasInitializedRef.current = true;

      // Show pulse dialog after checking backend
      const assignmentId = locationState[0].assignment.id;
      if (!checkedAssignments.has(assignmentId)) {
        const checkAndShowPulse = async () => {
          try {
            const studentId = getCurrentUser()!.id;
            // 只检查Start类型的问题
            const response = await checkPulseItem({ studentId, assignmentId, type: 'start' });

            console.log('START Pulse check response:', {
              assignmentId,
              shouldShow: response.data?.shouldShow,
              type: response.data?.type,
              itemId: response.data?.itemId
            });

            if (response.data?.shouldShow && response.data.type && response.data.itemId) {
              setPulseAssignmentId(assignmentId);
              setPulseType(response.data.type as 'start' | 'end');
              setPulseItemId(response.data.itemId);
              setShowPulseDialog(true);
              console.log('Will show START pulse item:', response.data.itemId);
            }
          } catch (error) {
            console.error('Failed to check pulse item:', error);
          }
        };

        setTimeout(() => checkAndShowPulse(), 500);
        setCheckedAssignments(prev => new Set(prev).add(assignmentId));
      }
    }
  }, [locationState]);

  useEffect(() => {
    const fetch = async () => {
      // Prevent concurrent fetches
      if (isFetchingRef.current) {
        return;
      }
      isFetchingRef.current = true;

      try {
        const map = new Map();
        const studentId = getCurrentUser()!.id;

        for (const assignmentModel of assignmentModels) {
          const assignmentId = assignmentModel.assignment.id!;
          const quizId = assignmentModel.assignment.quizId;
          console.log('assignmentId', assignmentId);
          const rsp = await getQuestionList({
            studentId: studentId,
            assignmentId: assignmentId,
            newest: true
          })

          // Fetch quiz to get language
          let language = 'zh-HK'; // default
          if (quizId) {
            const quizRsp = await getQuiz({ quizId: quizId });
            if (quizRsp.code === 200 && quizRsp.data?.quiz?.language) {
              language = quizRsp.data.quiz.language;
            }
          }

          if (rsp.code === 200 && rsp.data) {
            rsp.data.questionModelList = rsp.data.questionModelList.filter((value) => value !== null);
            if (!map.has(assignmentId)) {
              map.set(assignmentId, {...rsp.data, title: assignmentModel.assignment.title, language});
            }
          }
        }
        setQuizMap(map);
      } finally {
        isFetchingRef.current = false;
      }
    }

    if (assignmentModels && assignmentModels.length > 0) {
      fetch();
    }
  }, [assignmentModels])

  // Sync HOMEWORK_STATE.tabId when tabId changes
  useEffect(() => {
    if (tabId) {
      HOMEWORK_STATE.tabId = tabId;
    }
  }, [tabId]);

  if (!assignmentModels || assignmentModels.length === 0) {
    return (
      <ContentLayout title={t("student.homework")}>
        <div className="text-center py-8">{t("student.homework")}</div>
      </ContentLayout>
    );
  }

  // Ensure tabId is always set to the first assignment if empty
  const displayTabId = tabId || (assignmentModels.length > 0 ? assignmentModels[0].assignment.id?.toString() ?? '' : '');
  const subjectDisplayName = useSubjectDisplayName(assignmentModels[0]?.subjectId, assignmentModels[0]?.subject);

  const pageTitle = subjectDisplayName ? t("student.subjectHomework", { subject: subjectDisplayName }) : t("student.homework");

  // overlay/dialog:两态共用,只渲染一份(PulseItemDialog 有副作用,不可复制两份)
  const overlays = (
    <>
      <CongresOverlay isLoading={showCongres} text={t('student.done')}></CongresOverlay>

      {/* Pulse Item Dialog */}
      <PulseItemDialog
        open={showPulseDialog}
        type={pulseType}
        itemId={pulseItemId}
        onComplete={async (data) => {
          if (data && pulseAssignmentId) {
            try {
              // 循环提交每道题目的评分
              for (const item of data) {
                console.log('Submitting pulse item:', {
                  studentId: getCurrentUser()!.id,
                  assignmentId: pulseAssignmentId,
                  type: pulseType,
                  itemId: item.itemId,
                  rating: item.rating
                });

                await submitPulseItem({
                  studentId: getCurrentUser()!.id,
                  assignmentId: pulseAssignmentId,
                  type: pulseType,
                  itemId: item.itemId, // 使用具体的 itemId (S1a, S1b, S2a, S2b, etc.)
                  rating: item.rating
                });
              }
              console.log('Pulse item submitted successfully');
            } catch (error) {
              console.error('Failed to submit pulse item:', error);
            }
          }
          setShowPulseDialog(false);

          // 如果是 END pulse，完成后需要显示预期表现对话框
          if (pulseType === 'end') {
            setShouldShowExpectedDialog(true);
          }
        }}
      />
    </>
  );

  // 答题主体:桌面/横屏共享的同一段 JSX(同一时刻只渲染一棵树 → QuizCarousel 只挂一份)
  const answerBody = (
    <Tabs value={displayTabId} className="relative w-full md:min-h-0 md:flex-1" onValueChange={(value) => {
            HOMEWORK_STATE.tabId = value;
            setTabId(value);
          }}>
            {assignmentModels.length > 1 && (
              <div className="flex justify-center mb-6">
                <TabsList className="font-yusei-magic bg-muted-foreground/10 rounded-xl p-1 h-auto max-w-full flex-wrap justify-center gap-1">
                  {
                    assignmentModels.map((assignmentModel) => (
                      <TabsTrigger
                        key={assignmentModel.assignment.id}
                        value={assignmentModel.assignment.id!.toString()}
                        className="w-auto rounded-lg px-5 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md bg-transparent text-muted-foreground hover:bg-muted-foreground/10 data-[state=active]:hover:bg-primary"
                      >
                        {assignmentModel.assignment.title!}
                      </TabsTrigger>
                    ))
                  }
                </TabsList>
              </div>
            )}
            {
              assignmentModels.map((assignmentModel) => {
                const quizData = quizMap.get(assignmentModel.assignment.id!);
                return (
                  <TabsContent key={assignmentModel.assignment.id} value={assignmentModel.assignment.id!.toString()} className="md:min-h-0 md:flex md:flex-col">
                    {
                      quizMap.size > 0 && quizData && <QuizCarousel
                        quizProps={quizData}
                        fillHeight={!isMobileLandscape}
                        dueDate={assignmentModel.assignment.dueDate ? new Date(assignmentModel.assignment.dueDate) : undefined}
                        readonly={false}
                        shouldShowExpectedDialog={shouldShowExpectedDialog}
                        onExpectedDialogShown={() => setShouldShowExpectedDialog(false)}
                        onBeforeSubmit={async () => {
                          // 检查是否需要显示 END pulse 问题
                          try {
                            const response = await checkPulseItem({
                              studentId: getCurrentUser()!.id,
                              assignmentId: assignmentModel.assignment.id!,
                              type: 'end'
                            });

                            console.log('END Pulse check response:', {
                              assignmentId: assignmentModel.assignment.id,
                              shouldShow: response.data?.shouldShow,
                              type: response.data?.type,
                              itemId: response.data?.itemId
                            });

                            if (response.data?.shouldShow && response.data.type === 'end' && response.data.itemId) {
                              setPulseType('end');
                              setPulseItemId(response.data.itemId);
                              setPulseAssignmentId(assignmentModel.assignment.id!);
                              setShowPulseDialog(true);
                              console.log("Will show End pulse item:", response.data.itemId);
                              return true; // 返回 true 表示需要显示 END pulse
                            }
                          } catch (error) {
                            console.warn("Failed to check pulse item:", error);
                          }
                          return false; // 返回 false 表示不需要 END pulse
                        }}
                        onFinish={async () => {
                          setShowCongres(true);
                          setTimeout(() => {
                            setShowCongres(false);
                          }, 3000);
                        }}
                      ></QuizCarousel>
                    }
                  </TabsContent>
                );
              })
            }
    </Tabs>
  );

  if (isMobileLandscape) {
    return (
      <>
        {overlays}
        <MobileLandscapeAnswer title={pageTitle}>{answerBody}</MobileLandscapeAnswer>
      </>
    );
  }

  return (
    <>
      {overlays}
      <ContentLayout title={pageTitle} backgroundColor="var(--practice-page-bg)" className="md:h-full md:overflow-hidden">
        {/* lg/xl 左 padding 对齐 2xl 的 pl-0(内容贴近侧栏,gap≈24)；2xl/1920 不变 */}
        <div className="mx-auto w-full max-w-[2000px] px-6 pt-3 pb-6 lg:pl-0 lg:pr-6 2xl:pl-0 2xl:pr-6 min-[1920px]:max-w-none min-[1920px]:px-6 min-[1920px]:pt-6! md:flex md:min-h-0 md:flex-1 md:flex-col">
          {/* 返回学习总览 */}
          <div className="mb-2 flex items-center md:mb-3">
            <NavLink
              to={ROUTE.STUDENT.OVERVIEW}
              className="inline-flex items-center gap-0.5 text-sm font-medium text-[#64748B] transition hover:text-foreground"
            >
              <ChevronLeft className="size-4" />
              {t('homework.backToOverview', { defaultValue: '返回學習總覽' })}
            </NavLink>
          </div>
          {/* ≥md(含 1920+):灰卡自身不滚,改纵向 flex 让内部题号栏钉底、答题区局部滚动(去掉旧 1920 整卡滚动覆写,防题号栏掉出一屏) */}
          <div className="rounded-[30px] bg-[#F3F3F4] p-5 xl:p-6 min-[1920px]:p-10! xl:rounded-[38px] 2xl:ml-auto 2xl:w-full 2xl:max-w-[1433px] 2xl:rounded-[44px] min-[1920px]:max-w-none! min-[1920px]:rounded-[50px]! md:min-h-0 md:flex-1 md:flex md:flex-col md:overflow-hidden">
            {answerBody}
          </div>
        </div>
      </ContentLayout>
    </>
  );
};

export default withTranslation()(HomeworkPage);